import { useEffect, useState } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import { useForm, Controller } from 'react-hook-form';
import { makeCall } from 'utils/utils';
import { Record, UpdateFormData as FormData } from './types';

type PropsType = {
  open: boolean;
  handleClose: () => void;
  record: Record | null;
  refresh: () => void;
};

export default function Update({ open, handleClose, record, refresh }: PropsType) {
  const [isCopied, setIsCopied] = useState(false);
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm<FormData>();

  useEffect(() => {
    if (record) {
      reset({
        adminFirstname: record.adminFirstname,
        adminLastname: record.adminLastname,
        adminEmail: record.adminEmail,
        adminStatus: record.adminStatus,
      });
    }
  }, [record, reset]);

  const onSubmit = async (data: FormData) => {
    const payload = {
      ...data,
    };
    await makeCall(`admins/${record?.adminUuid}`, 'PUT', payload);
    close();
    refresh();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(record?.adminUuid ?? '');
    setIsCopied(true);
  };

  const close = () => {
    setIsCopied(false);
    handleClose();
  };

  return (
    <div>
      <Dialog open={open} disableBackdropClick={true} onClose={close} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">Update admin</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <DialogContentText>
              ID: {record?.adminUuid}{' '}
              <Button disabled={isCopied} color="primary" onClick={handleCopy}>
                {isCopied ? 'Copied' : 'Copy'}
              </Button>
            </DialogContentText>
            <Controller
              name="adminFirstname"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors.adminFirstname ? true : false}
                  helperText={errors.adminFirstname ? 'Admin first name is required!' : null}
                  autoFocus
                  margin="dense"
                  label="First Name"
                  fullWidth
                />
              )}
            />
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors.adminLastname ? true : false}
                  helperText={errors.adminLastname ? 'Admin last name is required!' : null}
                  margin="dense"
                  label="Last Name"
                  fullWidth
                />
              )}
              name="adminLastname"
              control={control}
              rules={{ required: true }}
            />
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors.adminEmail ? true : false}
                  helperText={errors.adminEmail ? 'Valid email is required!' : null}
                  margin="dense"
                  label="Email Address"
                  fullWidth
                />
              )}
              name="adminEmail"
              control={control}
              rules={{
                required: true,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: 'Valid email is required!',
                },
              }}
            />
            <Controller
              name="adminStatus"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} defaultChecked={record?.adminStatus} color="primary" />}
                  label="Active"
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={close} color="primary">
              Cancel
            </Button>
            <Button disabled={isSubmitting} type="submit" color="primary">
              {isSubmitting ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}
